import React, { useEffect, useState } from "react";
import { ActivityIndicator, Image, ImageBackground, PixelRatio, StyleSheet, Text, TouchableOpacity, View } from "react-native";
import { background, leftFoot, Icons } from "../../assets";
import LayoutWrapper from "../../components/LayoutWrapper/LayoutWrapper";
import { useNavigation } from "@react-navigation/native";
import { Scanfile } from "../../shared/model/types";
import ScannerService from "../../shared/services/scanner-service";
import { useGlobalState } from "../../shared/state";
import { Styles } from "../../styles";
import ScanFailedModal from "./ScanFailedModal";
import { I18N } from "../../shared/util";

const ScanLeftFoot: React.FC = (): JSX.Element => {
  const [, setCurrentScreen] = useGlobalState("currentScreen");
  const [currentScanner] = useGlobalState("currentScanner");
  const [newScanState] = useGlobalState("newScanState");
  const [language] = useGlobalState("language");
  const [scanfiles, setScanfiles] = useGlobalState("scanfiles");
  const [currentMeasurements] = useGlobalState("currentMeasurements");

  const [scanInProgress, setScanInProgress] = useState<boolean>(false);
  const [scanProcessResult, setScanProcessResult] = useState<"SUCCESS" | "FAILURE">();
  const [scannerParameter, setScannerParameter] = useState<any>({
    scannedObject: undefined,
    side: undefined,
    returnType: undefined,
  });
  const [errorCode, setErrorCode] = useState<string>("");

  const navigation = useNavigation();
  const i18n = I18N(language);

  const isBirkenstock = currentScanner && currentScanner.partner && currentScanner.partner?.toLowerCase() === "birkenstock";

  useEffect(() => {
    getScanParameter();
  }, []);

  useEffect(() => {
    if (scanfiles.length === 1) {
      navigate("ScanRightFoot");
    }
  }, [scanfiles]);

  const navigate = (route: string | undefined, options: any = undefined) => {
    if (route) {
      navigation.navigate(route, options);
      setCurrentScreen(route);
    }
  };

  const reset = () => {
    setScanProcessResult(undefined);
    setScanfiles([]);
    setScanInProgress(false);
  };

  const getScanParameter = () => {
    const mode = newScanState && newScanState.mode ? newScanState.mode.toLowerCase() : "socks";
    setScannerParameter({
      scannedObject: mode,
      side: "left",
      returnType: "base64",
    });
  };

  const getScan = () => {
    if (scannerParameter) {
      setScanInProgress(true);
      ScannerService.getScan(currentScanner["scanner-address"], scannerParameter)
        .then((scanResult: Scanfile) => {
          // Rename colouredSole to colored_sole if present
          if ("colouredSole" in scanResult) {
            scanResult.colored_sole = scanResult.colouredSole;
            delete scanResult.colouredSole;
          }
          setScanProcessResult("SUCCESS");
          setScanfiles([
            ...scanfiles,
            {
              side: scanResult.side,
              data: scanResult.data,
              format: scanResult.format,
              condition: "standing",
              raw_measurements: {
                arch_height: scanResult.rawMeasurements.archHeight,
                heel_angle: scanResult.rawMeasurements.heelAngle,
                heel_angle_heel: scanResult.rawMeasurements.heelAngleH,
                heel_angle_tendon: scanResult.rawMeasurements.heelAngleT,
              },
              categorized_measurements: {
                arch_height: {
                  category: scanResult.categorizedMeasurements.archHeight.category,
                  value: scanResult.categorizedMeasurements.archHeight.value,
                },
                heel_angle: {
                  category: scanResult.categorizedMeasurements.heelAngle.category,
                  value: scanResult.categorizedMeasurements.heelAngle.value,
                },
                heel_angle_heel: {
                  category: scanResult.categorizedMeasurements.heelAngleH.category,
                  value: scanResult.categorizedMeasurements.heelAngleH.value,
                },
                heel_angle_tendon: {
                  category: scanResult.categorizedMeasurements.heelAngleT.category,
                  value: scanResult.categorizedMeasurements.heelAngleT.value,
                },
                foot_type: {
                  category: scanResult.categorizedMeasurements.footType.category,
                  value: scanResult.categorizedMeasurements.footType.value,
                },
              },
              insole_recommendation: scanResult.InsoleRecommendation,
              plantar_image: scanResult.plantarColorImage,
              real_color_plantar_image: scanResult.plantarImage ?? "",
              colored_sole: scanResult.colored_sole, // always use the renamed property
            },
          ]);
        })
        .catch((error: any) => {
          console.log("error", error);
          if (error.response_code === 404) {
            setErrorCode("scannerBusy");
          } else {
            if (Array.isArray(error.message.errors)) {
              setErrorCode(error.message.errors[0].error_code);
            } else if (error.message && error.message.includes("foam")) {
              setErrorCode("foamBoxNotImplemented");
            } else {
              setErrorCode("genericError");
            }
          }
          setScanInProgress(false);
          setScanProcessResult("FAILURE");
        });
    }
  };

  return (
    <ImageBackground source={!isBirkenstock && background} style={Styles.backgroundImage} blurRadius={15}>
      <LayoutWrapper
        showHeaderCloseButton={false}
        showHeaderLogo={false}
        showMenuButtons={false}
        hideNewScanButton
        hideLeftButton
        hideInfoButton
        hideBackButton={isBirkenstock ? true : scanInProgress ? true : false}
        showBirkenstockFooter={isBirkenstock && true}
        showBirkenstockFooterLogo={isBirkenstock && true}
        showBirkenstockHomeButton={isBirkenstock && !scanInProgress && scanProcessResult && scanProcessResult !== "FAILURE" && true}
      >
        <View style={styles.contentWrapper}>
          {scanInProgress &&
            (isBirkenstock ? (
              <View
                style={{
                  flex: 1,
                  alignItems: "center",
                  justifyContent: "center",
                  width: "50%",
                  maxHeight: "50%",
                  shadowColor: "#000",
                  shadowOffset: { width: 6 / PixelRatio.get(), height: 6 / PixelRatio.get() },
                  shadowOpacity: 0.16,
                  shadowRadius: 4 / PixelRatio.get(),
                  elevation: 1,
                  backgroundColor: "white",
                  marginTop: "5%",
                }}
              >
                <Text style={[styles.processingText, { color: "#0e2288", fontSize: isBirkenstock ? 40 : 28, fontFamily: isBirkenstock && "Birkentype", fontWeight: "bold" }]}>
                  {i18n.t("scan.inProgress")}
                </Text>
                <Text style={[styles.processingText, { color: "#0e2288", fontSize: isBirkenstock ? 28 : 20, fontFamily: isBirkenstock && "Geist-Light" }]}>{i18n.t("scan.keepStill")}</Text>
                <Image source={Icons.progressIconBirkenstock} style={{ width: "15%", height: "20%", resizeMode: "contain" }} />
              </View>
            ) : (
              <View style={styles.processingWrapper}>
                <Text style={styles.processingText}>{i18n.t("scan.inProgress")}</Text>
                <ActivityIndicator size="large" color={"#2bc6ff"} />
              </View>
            ))}
          {!scanInProgress &&
            (!scanProcessResult || scanProcessResult !== "FAILURE") &&
            (isBirkenstock ? (
              <View style={{ flex: 1, alignItems: "center", justifyContent: "flex-start", width: "100%", maxHeight: "98%" }}>
                <View style={{ flex: 1, alignItems: "center", justifyContent: "space-between", width: "70%", maxHeight: "92%", flexDirection: "row" }}>
                  <View style={{ flex: 1, alignItems: "center", justifyContent: "space-between", maxWidth: "25%", height: "85%" }}>
                    <Text style={[styles.headerText, { color: "#0e2288", maxHeight: "10%", marginTop: 0, fontSize: 48, fontFamily: "Birkentype" }]}>{i18n.t("scan.left").toUpperCase()}</Text>
                    <View style={[styles.contentBox, { width: "90%", maxHeight: "85%" }]}>
                      <View style={styles.imageContainer}>
                        <Image source={Icons.leftFootIconBirkenstock} style={[styles.birkenstockImage, { maxHeight: "60%" }]} />
                      </View>
                    </View>
                  </View>
                  <View style={{ flex: 1, alignItems: "center", justifyContent: "space-between", maxWidth: "70%", height: "85%" }}>
                    <Text style={[styles.headerText, { color: "#0e2288", maxHeight: "10%", marginTop: 0, fontSize: 48, fontFamily: "Birkentype" }]}>{i18n.t("scan.instructions").toUpperCase()}</Text>
                    <View style={[styles.contentBox, { width: "95%", maxHeight: "85%", flexDirection: "column", justifyContent: "space-evenly", padding: "2%", margin: 0 }]}>
                      <View style={{ flexDirection: "row", alignItems: "center", width: "90%", height: "28%" }}>
                        <View style={styles.imageContainerWithShadow}>
                          <Image source={Icons.leftFootRollPantsBirkenstock} style={styles.birkenstockImage} />
                        </View>
                        <Text style={[styles.start, { maxWidth: "80%", color: "#0e2288", fontSize: 22, fontFamily: "Geist-Light" }]}>{i18n.t("scan.rollUpLegwear")}</Text>
                      </View>
                      <View style={{ flexDirection: "row", alignItems: "center", width: "90%", height: "28%" }}>
                        <View style={styles.imageContainerWithShadow}>
                          <Image style={styles.birkenstockImage} source={Icons.leftFootPositionBirkenstock} />
                        </View>
                        <Text style={[styles.start, { maxWidth: "80%", color: "#0e2288", fontSize: 22, fontFamily: "Geist-Light" }]}>{i18n.t("scan.positionFeet")}</Text>
                      </View>
                      <View style={{ flexDirection: "row", alignItems: "center", width: "90%", height: "28%" }}>
                        <View style={styles.imageContainerWithShadow}>
                          <Image style={styles.birkenstockImage} source={Icons.bothFeetBirkenstock} />
                        </View>
                        <Text style={[styles.start, { maxWidth: "80%", color: "#0e2288", fontSize: 22, fontFamily: "Geist-Light" }]}>{i18n.t("scan.distributeWeight")}</Text>
                      </View>
                    </View>
                  </View>
                </View>
                <View style={{ width: "100%", height: "10%", alignItems: "center", justifyContent: "center" }}>
                  <TouchableOpacity
                    style={{ width: 335, padding: 0, height: 77, backgroundColor: "#0e2288", alignItems: "center", justifyContent: "center", borderRadius: 10 }}
                    activeOpacity={0.6}
                    onPress={getScan}
                  >
                    <Text style={{ color: "white", fontSize: 36, fontWeight: "bold", textAlign: "center", fontFamily: "Birkentype" }}>{i18n.t("scan.start")}</Text>
                  </TouchableOpacity>
                </View>
              </View>
            ) : (
              <>
                <View style={styles.headerRow}>
                  <Text style={styles.headerText}>{i18n.t("scan.captionLeft")}</Text>
                </View>
                <TouchableOpacity style={styles.contentBox} activeOpacity={0.6} onPress={getScan}>
                  <View style={styles.captionContainer}>
                    <Text style={styles.start}>{i18n.t("scan.start")}</Text>
                  </View>
                  <View style={styles.imageContainer}>
                    <Image source={leftFoot} style={styles.image} />
                  </View>
                  <View style={styles.captionContainer}>
                    <Text style={styles.caption}>{i18n.t("scan.left")}</Text>
                  </View>
                </TouchableOpacity>
              </>
            ))}
          {!scanInProgress && scanProcessResult && scanProcessResult === "FAILURE" && (
            <>
              <ScanFailedModal errorCode={errorCode} onScanAgain={() => reset()} onCancel={() => navigate(currentMeasurements ? "Measurements" : "Home")} />
            </>
          )}
        </View>
      </LayoutWrapper>
    </ImageBackground>
  );
};

export default ScanLeftFoot;

const styles = StyleSheet.create({
  contentWrapper: {
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
  },
  headerRow: {
    maxHeight: "15%",
    width: "100%",
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  headerText: {
    fontFamily: "Montserrat-Black",
    fontSize: 21,
    fontWeight: "900",
    color: "#3a3a3a",
    marginTop: "1.5%",
  },
  contentBox: {
    backgroundColor: "#ffffff",
    opacity: 0.9,
    width: "40%",
    maxHeight: "75%",
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 6 / PixelRatio.get(), height: 6 / PixelRatio.get() },
    shadowOpacity: 0.16,
    shadowRadius: 4 / PixelRatio.get(),
    elevation: 1,
  },
  imageContainer: {
    height: "65%",
    width: "100%",
    flex: 1,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  image: {
    height: "100%",
    width: "50%",
    maxWidth: "75%",
    resizeMode: "contain",
    marginTop: "2%",
  },
  captionContainer: {
    height: "10%",
    marginTop: "7%",
  },
  caption: {
    fontFamily: "Montserrat-Black",
    fontSize: 18,
    color: "#3a3a3a",
  },
  start: {
    fontFamily: "Montserrat-Black",
    fontSize: 18,
    color: "#3a3a3a",
  },
  processingWrapper: {
    minHeight: "90%",
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  processingText: {
    fontFamily: "Montserrat-Black",
    fontSize: 30,
    marginBottom: 15,
  },
  checkmark: {
    maxHeight: "75%",
    maxWidth: "75%",
    resizeMode: "contain",
  },
  imageContainerWithShadow: {
    maxWidth: "15%",
    width: "15%",
    height: "100%",
    marginRight: "2%",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.16,
    shadowRadius: 4,
    elevation: 4,
    alignContent: "center",
    justifyContent: "center",
    padding: "0.5%",
  },
  birkenstockImage: {
    height: "100%",
    width: "100%",
    resizeMode: "contain",
  },
});
